import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Database, 
  Cpu, 
  HardDrive, 
  Activity, 
  Lock, 
  Search, 
  BarChart3, 
  Users, 
  MessageSquare, 
  Code, 
  Shield, 
  Columns, 
  Zap, 
  Network, 
  Settings,
  ArrowLeft,
  RefreshCw,
  Download,
  Eye
} from 'lucide-react';

// DMV Category definitions
interface DMVCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  dmvCount: number;
  keyDMVs: string[];
  useCase: string;
}

const dmvCategories: DMVCategory[] = [
  {
    id: 'database',
    name: 'Database Related',
    description: 'Database files, space usage, sessions, and database-level statistics',
    icon: <Database className="w-6 h-6" />,
    color: 'blue',
    dmvCount: 42,
    keyDMVs: [
      'dm_db_file_space_usage', 'dm_db_session_space_usage', 'dm_db_partition_stats', 'dm_db_index_usage_stats',
      'dm_db_index_physical_stats', 'dm_db_index_operational_stats', 'dm_db_missing_index_details', 'dm_db_missing_index_groups',
      'dm_db_missing_index_group_stats', 'dm_db_missing_index_columns', 'dm_db_database_page_allocations', 'dm_db_page_info',
      'dm_db_persisted_sku_features', 'dm_db_stats_properties', 'dm_db_stats_histogram', 'dm_db_incremental_stats_properties',
      'dm_db_column_store_row_group_physical_stats', 'dm_db_column_store_row_group_operational_stats', 'dm_db_xtp_checkpoint_files',
      'dm_db_xtp_checkpoint_stats', 'dm_db_xtp_gc_cycle_stats', 'dm_db_xtp_hash_index_stats', 'dm_db_xtp_index_stats',
      'dm_db_xtp_memory_consumers', 'dm_db_xtp_merge_requests', 'dm_db_xtp_nonclustered_index_stats', 'dm_db_xtp_object_stats',
      'dm_db_xtp_table_memory_stats', 'dm_db_xtp_transactions', 'dm_db_task_space_usage', 'dm_db_uncontained_entities',
      'dm_db_wait_stats', 'dm_db_resource_stats', 'dm_db_log_info', 'dm_db_log_stats', 'dm_db_log_space_usage',
      'dm_db_file_space_usage', 'dm_db_fts_index_physical_stats', 'dm_db_tuning_recommendations', 'dm_db_objects_disabled_on_compatibility_level_change',
      'dm_db_objects_impacted_on_version_change', 'dm_db_column_store_row_group_metadata'
    ],
    useCase: 'Monitor database size, file growth, and space utilization'
  },
  {
    id: 'execution',
    name: 'Execution Related',
    description: 'Query execution, plans, requests, sessions, and performance statistics',
    icon: <Activity className="w-6 h-6" />,
    color: 'green',
    dmvCount: 48,
    keyDMVs: [
      'dm_exec_requests', 'dm_exec_sessions', 'dm_exec_query_stats', 'dm_exec_procedure_stats',
      'dm_exec_connections', 'dm_exec_sql_text', 'dm_exec_query_plan', 'dm_exec_text_query_plan',
      'dm_exec_cached_plans', 'dm_exec_plan_attributes', 'dm_exec_query_memory_grants', 'dm_exec_query_resource_semaphores',
      'dm_exec_background_job_queue', 'dm_exec_background_job_queue_stats', 'dm_exec_compute_node_errors', 'dm_exec_compute_node_status',
      'dm_exec_compute_nodes', 'dm_exec_distributed_request_steps', 'dm_exec_distributed_requests', 'dm_exec_distributed_sql_requests',
      'dm_exec_dms_services', 'dm_exec_dms_workers', 'dm_exec_external_operations', 'dm_exec_external_work',
      'dm_exec_function_stats', 'dm_exec_query_optimizer_info', 'dm_exec_query_optimizer_memory_gateways', 'dm_exec_query_parallel_workers',
      'dm_exec_query_profiles', 'dm_exec_query_transformation_stats', 'dm_exec_session_wait_stats', 'dm_exec_trigger_stats',
      'dm_exec_valid_use_hints', 'dm_exec_xml_handles', 'dm_exec_describe_first_result_set', 'dm_exec_describe_first_result_set_for_object',
      'dm_exec_input_buffer', 'dm_exec_plan_stats', 'dm_exec_procedure_stats', 'dm_exec_query_plan_stats',
      'dm_exec_requests', 'dm_exec_sessions', 'dm_exec_connections', 'dm_exec_cursors',
      'dm_exec_query_statistics_xml', 'dm_exec_sql_text', 'dm_exec_text_query_plan', 'dm_exec_cached_plan_dependent_objects'
    ],
    useCase: 'Track running queries, execution plans, and performance metrics'
  },
  {
    id: 'operating_system',
    name: 'Operating System',
    description: 'Memory, CPU, wait statistics, schedulers, and system resources',
    icon: <Cpu className="w-6 h-6" />,
    color: 'orange',
    dmvCount: 32,
    keyDMVs: [
      'dm_os_wait_stats', 'dm_os_memory_clerks', 'dm_os_schedulers', 'dm_os_performance_counters',
      'dm_os_sys_info', 'dm_os_sys_memory', 'dm_os_process_memory', 'dm_os_memory_cache_counters',
      'dm_os_memory_cache_clock_hands', 'dm_os_memory_cache_entries', 'dm_os_memory_cache_hash_tables', 'dm_os_memory_objects',
      'dm_os_memory_pools', 'dm_os_nodes', 'dm_os_buffer_descriptors', 'dm_os_buffer_pool_extension_configuration',
      'dm_os_cluster_nodes', 'dm_os_cluster_properties', 'dm_os_dispatcher_pools', 'dm_os_enumerate_fixed_drives',
      'dm_os_file_exists', 'dm_os_host_info', 'dm_os_hosts', 'dm_os_latch_stats',
      'dm_os_loaded_modules', 'dm_os_ring_buffers', 'dm_os_spinlock_stats', 'dm_os_stacks',
      'dm_os_sublatches', 'dm_os_sys_info', 'dm_os_tasks', 'dm_os_threads',
      'dm_os_virtual_address_dump', 'dm_os_waiting_tasks', 'dm_os_windows_info', 'dm_os_workers'
    ],
    useCase: 'Monitor CPU, memory usage, and system-level performance'
  },
  {
    id: 'io_related',
    name: 'I/O Related',
    description: 'I/O statistics, pending requests, virtual file stats, and disk performance',
    icon: <HardDrive className="w-6 h-6" />,
    color: 'purple',
    dmvCount: 25,
    keyDMVs: [
      'dm_io_virtual_file_stats', 'dm_io_pending_io_requests', 'dm_io_backup_tapes', 'dm_io_cluster_shared_drives',
      'dm_io_cluster_valid_path_names', 'dm_io_virtual_file_stats', 'dm_filestream_file_io_handles', 'dm_filestream_file_io_requests',
      'dm_filestream_non_transacted_handles', 'dm_continuous_copy_status', 'dm_geo_replication_link_status', 'dm_operation_status',
      'dm_user_db_resource_governance', 'dm_resource_governor_configuration', 'dm_resource_governor_external_resource_pools', 'dm_resource_governor_external_resource_pool_affinity',
      'dm_resource_governor_resource_pools', 'dm_resource_governor_resource_pool_affinity', 'dm_resource_governor_resource_pool_volumes', 'dm_resource_governor_workload_groups',
      'dm_workload_management_workload_classifier_details', 'dm_workload_management_workload_classifiers', 'dm_workload_management_workload_groups_stats', 'dm_pdw_diag_processing_stats',
      'dm_pdw_dms_cores'
    ],
    useCase: 'Analyze disk I/O performance and identify bottlenecks'
  },
  {
    id: 'transaction',
    name: 'Transaction Related',
    description: 'Transactions, locks, version store, and concurrency control',
    icon: <Lock className="w-6 h-6" />,
    color: 'red',
    dmvCount: 22,
    keyDMVs: [
      'dm_tran_locks', 'dm_tran_session_transactions', 'dm_tran_version_store', 'dm_tran_active_transactions',
      'dm_tran_current_snapshot', 'dm_tran_current_transaction', 'dm_tran_database_transactions', 'dm_tran_top_version_generators',
      'dm_tran_transactions_snapshot', 'dm_tran_version_store_space_usage', 'dm_tran_active_snapshot_database_transactions', 'dm_tran_commit_table',
      'dm_tran_global_recovery_transactions', 'dm_tran_global_transactions', 'dm_tran_persistent_version_store_stats', 'dm_tran_aborted_transactions',
      'dm_hadr_auto_page_repair', 'dm_hadr_automatic_seeding', 'dm_hadr_availability_group_states', 'dm_hadr_availability_replica_cluster_nodes',
      'dm_hadr_availability_replica_cluster_states', 'dm_hadr_availability_replica_states'
    ],
    useCase: 'Monitor locking, blocking, and transaction isolation'
  },
  {
    id: 'index_related',
    name: 'Index Related',
    description: 'Index usage, fragmentation, missing indexes, and optimization',
    icon: <Search className="w-6 h-6" />,
    color: 'indigo',
    dmvCount: 18,
    keyDMVs: ['dm_db_index_usage_stats', 'dm_db_missing_index_details', 'dm_db_index_physical_stats', 'dm_db_index_operational_stats'],
    useCase: 'Optimize indexes and identify missing or unused indexes'
  },
  {
    id: 'replication',
    name: 'Replication',
    description: 'Replication agents, publications, subscriptions, and distribution',
    icon: <Network className="w-6 h-6" />,
    color: 'teal',
    dmvCount: 19,
    keyDMVs: ['dm_repl_articles', 'dm_repl_schemas', 'dm_repl_traninfo', 'dm_repl_tranhash'],
    useCase: 'Monitor replication health and performance'
  },
  {
    id: 'always_on',
    name: 'Always On AG',
    description: 'Availability Groups, replicas, databases, and high availability',
    icon: <Shield className="w-6 h-6" />,
    color: 'emerald',
    dmvCount: 16,
    keyDMVs: ['dm_hadr_availability_replica_states', 'dm_hadr_database_replica_states', 'dm_hadr_cluster', 'dm_hadr_availability_group_states'],
    useCase: 'Monitor Always On Availability Groups health and failover'
  },
  {
    id: 'fulltext',
    name: 'Full-Text Search',
    description: 'Full-text catalogs, indexes, populations, and search performance',
    icon: <Search className="w-6 h-6" />,
    color: 'amber',
    dmvCount: 12,
    keyDMVs: ['dm_fts_index_keywords', 'dm_fts_memory_buffers', 'dm_fts_memory_pools', 'dm_fts_outstanding_batches'],
    useCase: 'Monitor full-text search indexing and query performance'
  },
  {
    id: 'service_broker',
    name: 'Service Broker',
    description: 'Message queuing, conversations, endpoints, and asynchronous processing',
    icon: <MessageSquare className="w-6 h-6" />,
    color: 'pink',
    dmvCount: 14,
    keyDMVs: ['dm_broker_activated_tasks', 'dm_broker_connections', 'dm_broker_forwarded_messages', 'dm_broker_queue_monitors'],
    useCase: 'Monitor Service Broker message processing and queues'
  },
  {
    id: 'clr_integration',
    name: 'CLR Integration',
    description: '.NET CLR assemblies, procedures, and managed code execution',
    icon: <Code className="w-6 h-6" />,
    color: 'violet',
    dmvCount: 8,
    keyDMVs: ['dm_clr_appdomains', 'dm_clr_loaded_assemblies', 'dm_clr_properties', 'dm_clr_tasks'],
    useCase: 'Monitor .NET CLR integration and managed code performance'
  },
  {
    id: 'resource_governor',
    name: 'Resource Governor',
    description: 'Workload groups, resource pools, and resource management',
    icon: <BarChart3 className="w-6 h-6" />,
    color: 'cyan',
    dmvCount: 11,
    keyDMVs: ['dm_resource_governor_configuration', 'dm_resource_governor_resource_pools', 'dm_resource_governor_workload_groups', 'dm_resource_governor_resource_pool_affinity'],
    useCase: 'Monitor and manage resource allocation and workload prioritization'
  },
  {
    id: 'columnstore',
    name: 'Columnstore',
    description: 'Columnstore indexes, row groups, segments, and compression',
    icon: <Columns className="w-6 h-6" />,
    color: 'lime',
    dmvCount: 9,
    keyDMVs: ['dm_db_column_store_row_group_physical_stats', 'dm_db_column_store_row_group_operational_stats', 'dm_column_store_object_pool', 'dm_db_columnstore_rowgroup_metadata'],
    useCase: 'Optimize columnstore indexes and monitor compression efficiency'
  },
  {
    id: 'memory_oltp',
    name: 'In-Memory OLTP',
    description: 'Memory-optimized tables, hash indexes, and Hekaton engine',
    icon: <Zap className="w-6 h-6" />,
    color: 'rose',
    dmvCount: 17,
    keyDMVs: ['dm_db_xtp_checkpoint_files', 'dm_db_xtp_hash_index_stats', 'dm_db_xtp_memory_consumers', 'dm_db_xtp_table_memory_stats'],
    useCase: 'Monitor In-Memory OLTP performance and memory usage'
  },
  {
    id: 'miscellaneous',
    name: 'Miscellaneous',
    description: 'Audit, security, backup, maintenance, and other specialized DMVs',
    icon: <Settings className="w-6 h-6" />,
    color: 'gray',
    dmvCount: 23,
    keyDMVs: ['dm_audit_actions', 'dm_server_audit_status', 'dm_cryptographic_provider_properties', 'dm_continuous_copy_status'],
    useCase: 'Monitor security, auditing, backup operations, and system maintenance'
  }
];

export function DMVDashboard() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [connectionId, setConnectionId] = useState<string>('');

  const getColorClasses = (color: string) => {
    const colorMap: Record<string, { bg: string; text: string; border: string; hover: string }> = {
      blue: { bg: 'bg-shiftleft-50', text: 'text-shiftleft-700', border: 'border-shiftleft-200', hover: 'hover:bg-shiftleft-100' },
      green: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', hover: 'hover:bg-green-100' },
      orange: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', hover: 'hover:bg-orange-100' },
      purple: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', hover: 'hover:bg-purple-100' },
      red: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', hover: 'hover:bg-red-100' },
      indigo: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', hover: 'hover:bg-indigo-100' },
      teal: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', hover: 'hover:bg-teal-100' },
      emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', hover: 'hover:bg-emerald-100' },
      amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', hover: 'hover:bg-amber-100' },
      pink: { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200', hover: 'hover:bg-pink-100' },
      violet: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', hover: 'hover:bg-violet-100' },
      cyan: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', hover: 'hover:bg-cyan-100' },
      lime: { bg: 'bg-lime-50', text: 'text-lime-700', border: 'border-lime-200', hover: 'hover:bg-lime-100' },
      rose: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', hover: 'hover:bg-rose-100' },
      gray: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', hover: 'hover:bg-gray-100' }
    };
    return colorMap[color] || colorMap.gray;
  };

  if (selectedCategory) {
    const category = dmvCategories.find(c => c.id === selectedCategory);
    if (!category) return null;

    return <DMVCategoryDetail category={category} onBack={() => setSelectedCategory(null)} connectionId={connectionId} />;
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-gray-900 flex items-center justify-center gap-3">
          <Database className="w-10 h-10 text-shiftleft-600" />
          SQL Server DMV Dashboard
        </h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Comprehensive monitoring of SQL Server 2019's 300+ Dynamic Management Views across 15 key categories.
          Click any category to explore detailed DMV information and real-time data.
        </p>
        
        {/* Connection Input */}
        <div className="flex items-center justify-center gap-3 mt-6">
          <label className="text-sm font-medium text-gray-700">Connection ID:</label>
          <input
            type="text"
            value={connectionId}
            onChange={(e) => setConnectionId(e.target.value)}
            placeholder="Enter SQL Server connection ID"
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-shiftleft-500 focus:border-shiftleft-500"
          />
        </div>
      </div>

      {/* Statistics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="text-center">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-shiftleft-600">300+</div>
            <div className="text-sm text-gray-600">Total DMVs</div>
          </CardContent>
        </Card>
        <Card className="text-center">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-green-600">15</div>
            <div className="text-sm text-gray-600">Categories</div>
          </CardContent>
        </Card>
        <Card className="text-center">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-purple-600">Real-time</div>
            <div className="text-sm text-gray-600">Monitoring</div>
          </CardContent>
        </Card>
        <Card className="text-center">
          <CardContent className="pt-6">
            <div className="text-3xl font-bold text-orange-600">SQL 2019</div>
            <div className="text-sm text-gray-600">Compatible</div>
          </CardContent>
        </Card>
      </div>

      {/* DMV Category Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dmvCategories.map((category) => {
          const colors = getColorClasses(category.color);
          
          return (
            <Card 
              key={category.id}
              className={`cursor-pointer transition-all duration-200 ${colors.bg} ${colors.border} ${colors.hover} border-2 hover:shadow-lg hover:scale-105`}
              onClick={() => setSelectedCategory(category.id)}
            >
              <CardHeader className="pb-3">
                <CardTitle className={`flex items-center gap-3 ${colors.text}`}>
                  {category.icon}
                  <div>
                    <div className="font-semibold">{category.name}</div>
                    <Badge variant="outline" className="mt-1">
                      {category.dmvCount} DMVs
                    </Badge>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  {category.description}
                </p>
                
                <div className="space-y-2">
                  <div className="text-xs font-medium text-gray-700">Key DMVs:</div>
                  <div className="flex flex-wrap gap-1">
                    {category.keyDMVs.slice(0, 2).map((dmv) => (
                      <Badge key={dmv} variant="secondary" className="text-xs">
                        {dmv.replace('dm_', '')}
                      </Badge>
                    ))}
                    {category.keyDMVs.length > 2 && (
                      <Badge variant="secondary" className="text-xs">
                        +{category.keyDMVs.length - 2} more
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="text-xs text-gray-600">
                    <strong>Use Case:</strong> {category.useCase}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-gray-500 mt-8 pt-6 border-t">
        <p>SQL Server 2019 Dynamic Management Views Dashboard • Real-time Performance Monitoring</p>
      </div>
    </div>
  );
}

// DMV Category Detail Component
interface DMVCategoryDetailProps {
  category: DMVCategory;
  onBack: () => void;
  connectionId: string;
}

function DMVCategoryDetail({ category, onBack, connectionId }: DMVCategoryDetailProps) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [selectedDMV, setSelectedDMV] = useState<string>('');
  const [autoRefresh, setAutoRefresh] = useState(false);

  const colors = {
    blue: { bg: 'bg-shiftleft-50', text: 'text-shiftleft-700', border: 'border-shiftleft-200' },
    green: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
    red: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
    teal: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
    pink: { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
    violet: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' },
    cyan: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
    lime: { bg: 'bg-lime-50', text: 'text-lime-700', border: 'border-lime-200' },
    rose: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
    gray: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' }
  }[category.color] || { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' };

  const loadDMVData = async (dmvName: string) => {
    if (!connectionId || !dmvName) return;
    
    setLoading(true);
    try {
      // This would call your existing SQL monitoring API
      const response = await fetch(`/api/sql-monitor/dmv/${connectionId}/${dmvName}`);
      const result = await response.json();
      
      if (result.success) {
        setData(result.data || []);
      } else {
        console.error('Failed to load DMV data:', result.message);
        setData([]);
      }
    } catch (error) {
      console.error('Error loading DMV data:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={onBack} className="flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Button>
        
        <div className="flex-1">
          <h1 className={`text-3xl font-bold flex items-center gap-3 ${colors.text}`}>
            {category.icon}
            {category.name} DMVs
          </h1>
          <p className="text-gray-600 mt-1">{category.description}</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="autoRefresh"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="autoRefresh" className="text-sm">Auto refresh</label>
          </div>
          
          <Button 
            variant="outline" 
            onClick={() => selectedDMV && loadDMVData(selectedDMV)}
            disabled={!selectedDMV || loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* DMV Selection and Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* DMV List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Available DMVs ({category.dmvCount})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {category.keyDMVs.map((dmv) => (
                <div
                  key={dmv}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedDMV === dmv 
                      ? `${colors.bg} ${colors.border} ${colors.text}` 
                      : 'hover:bg-gray-50 border-gray-200'
                  }`}
                  onClick={() => {
                    setSelectedDMV(dmv);
                    loadDMVData(dmv);
                  }}
                >
                  <div className="font-medium text-sm">sys.{dmv}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Click to view data
                  </div>
                </div>
              ))}
              
              {/* Show placeholder for additional DMVs */}
              {category.dmvCount > category.keyDMVs.length && (
                <div className="p-3 rounded-lg border border-dashed border-gray-300 text-center">
                  <div className="text-sm text-gray-500">
                    +{category.dmvCount - category.keyDMVs.length} additional DMVs
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    Full list available in production
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* DMV Data Display */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                {selectedDMV ? `sys.${selectedDMV}` : 'Select a DMV to view data'}
              </div>
              
              {selectedDMV && data.length > 0 && (
                <Button variant="outline" size="sm" className="flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Export CSV
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedDMV ? (
              <div className="text-center py-12 text-gray-500">
                <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a DMV from the list to view its data</p>
              </div>
            ) : loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-shiftleft-500" />
                <p className="text-gray-600">Loading DMV data...</p>
              </div>
            ) : !connectionId ? (
              <div className="text-center py-12 text-gray-500">
                <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Please provide a connection ID to view DMV data</p>
              </div>
            ) : data.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No data available for this DMV</p>
                <p className="text-sm mt-2">The DMV might be empty or require specific conditions</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      {Object.keys(data[0]).map((column) => (
                        <th key={column} className="text-left p-2 font-medium text-gray-700">
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.slice(0, 50).map((row, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                        {Object.values(row).map((value: any, colIdx) => (
                          <td key={colIdx} className="p-2 text-gray-900">
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {data.length > 50 && (
                  <div className="text-center py-4 text-sm text-gray-500">
                    Showing first 50 rows of {data.length} total rows
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>


    </div>
  );
}